import React, { useMemo } from "react";

/**
 * MetricSparkline — a soft, smoothed sparkline with baseline scaffold,
 * fill wash, and an endpoint accent. One chart shape for every metric card.
 *
 * Props:
 *   - data: array of { value: number } (timestamp ignored, points are evenly spaced)
 *   - color: stroke color (defaults to var(--gmb-primary))
 *   - height: px (default 88)
 */
function MetricSparkline({ data, color = "var(--gmb-primary)", height = 88, gradient = true, opacity = 0.6, smooth = true }) {
  const points = useMemo(() => {
    const values = (Array.isArray(data) ? data : [])
      .map((d) => Number(d?.value))
      .filter((v) => Number.isFinite(v));
    if (values.length === 0) return [];
    if (values.length === 1) return [values[0], values[0]];
    return values;
  }, [data]);

  const W = 320;
  const H = height;
  const PAD_X = 6;
  const PAD_TOP = 10;
  const PAD_BOTTOM = 10;
  const innerW = W - PAD_X * 2;
  const innerH = H - PAD_TOP - PAD_BOTTOM;

  const { d, fillD, dotX, dotY, hasShape } = useMemo(() => {
    if (points.length < 2) return { d: "", fillD: "", dotX: 0, dotY: 0, hasShape: false };

    // 7-day centered moving average — blends single-day spikes into the
    // surrounding week so the line flows like a wave instead of zigzagging
    // around one outlier day. Window auto-shrinks at the ends so the
    // start/finish stay anchored to real data.
    const window = smooth ? Math.min(7, Math.max(1, Math.floor(points.length / 4))) : 1;
    const half = Math.floor(window / 2);
    const smoothed = points.map((_, i) => {
      const start = Math.max(0, i - half);
      const end = Math.min(points.length, i + half + 1);
      let sum = 0;
      let count = 0;
      for (let j = start; j < end; j += 1) {
        sum += points[j];
        count += 1;
      }
      return count > 0 ? sum / count : 0;
    });

    // Square-root scale on the y-axis: further compresses tall peaks so the
    // chart focuses on shape, not absolute height. The exact value still
    // lives in the big number above the chart — this just controls visuals.
    const transformed = smoothed.map((v) => Math.sqrt(Math.max(0, v)));
    const min = Math.min(...transformed);
    const max = Math.max(...transformed);
    const range = max - min || 1;
    const stepX = innerW / (points.length - 1);

    // Leave a little headroom at the top so the peak doesn't kiss the edge.
    const PEAK_HEADROOM = 0.88;
    const xy = transformed.map((tv, i) => ({
      x: PAD_X + i * stepX,
      y: PAD_TOP + innerH - ((tv - min) / range) * innerH * PEAK_HEADROOM,
    }));

    const tension = 0.35;
    const segments = [`M ${xy[0].x},${xy[0].y}`];
    for (let i = 0; i < xy.length - 1; i += 1) {
      const p0 = xy[i - 1] || xy[i];
      const p1 = xy[i];
      const p2 = xy[i + 1];
      const p3 = xy[i + 2] || p2;
      const cp1x = p1.x + ((p2.x - p0.x) * tension) / 3;
      const cp1y = p1.y + ((p2.y - p0.y) * tension) / 3;
      const cp2x = p2.x - ((p3.x - p1.x) * tension) / 3;
      const cp2y = p2.y - ((p3.y - p1.y) * tension) / 3;
      segments.push(`C ${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${p2.x.toFixed(2)},${p2.y.toFixed(2)}`);
    }
    const baseline = H - PAD_BOTTOM;
    const linePath = segments.join(" ");
    const fillPath = `${linePath} L ${xy[xy.length - 1].x},${baseline} L ${xy[0].x},${baseline} Z`;

    return {
      d: linePath,
      fillD: fillPath,
      dotX: xy[xy.length - 1].x,
      dotY: xy[xy.length - 1].y,
      hasShape: true,
    };
  }, [points, innerW, innerH, H, smooth]);

  const gradientId = useMemo(
    () => `spark-fill-${Math.floor(Math.random() * 1e9)}`,
    []
  );

  if (!hasShape) {
    return (
      <div style={{ height }} className="w-full flex items-center justify-center">
        <div className="h-px w-full bg-gray-100" />
      </div>
    );
  }

  // Three faint horizontal scaffold lines at 25 / 50 / 75 % of inner height
  const guideYs = [0.25, 0.5, 0.75].map((p) => PAD_TOP + innerH * p);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      width="100%"
      height={H}
      style={{ display: "block", overflow: "visible" }}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={Math.min(0.5, Math.max(0.05, opacity * 0.4))} />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Baseline scaffold */}
      {guideYs.map((y) => (
        <line
          key={y}
          x1={PAD_X}
          x2={W - PAD_X}
          y1={y}
          y2={y}
          stroke="#E4E7EC"
          strokeWidth="1"
          strokeDasharray="2 4"
          vectorEffect="non-scaling-stroke"
        />
      ))}

      {/* Soft fill */}
      {gradient && <path d={fillD} fill={`url(#${gradientId})`} />}

      {/* Smoothed line — premium dashboard weight: thin and at near-full opacity
          since the color itself is already a lightened tint of the brand. */}
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeOpacity="0.95"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />

      {/* Endpoint dot with white halo */}
      <circle cx={dotX} cy={dotY} r="4.5" fill="#FFFFFF" />
      <circle cx={dotX} cy={dotY} r="3" fill={color} />
    </svg>
  );
}

export default MetricSparkline;
